import React, { useEffect } from 'react'
import { Controller, useForm } from 'react-hook-form'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { toast } from 'react-toastify'
import { Autocomplete, Box, Button, TextField, Typography } from '@mui/material'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import TechnologyCompanyApi, {
  TechCompanyBackendData
} from 'api/b2b/TechnologyCompanyApi'
import { findByValue } from 'helpers/b2b'
import OrbitSession from 'hooks/useCookies'
import { CurrencySupported } from 'pages/B2B/TDP/Registration/contants'

import { commissionTypes } from '../constants'

function AddTechnologyCompany() {
  type SelectOption = { label: string; value: string }
  const navigate = useNavigate()

  type FormValues = {
    commissionType: SelectOption | null
    companyName: string
    currency: SelectOption | null
    value: string
    // ...others
  }
  const queryClient = useQueryClient()

  const [searchParams] = useSearchParams()
  const mode = searchParams.get('mode')
  const companyId = searchParams.get('id') || ''
  const faberToken = OrbitSession.get('faberToken')
  const userEmail = OrbitSession.get('email') || ''

  const shouldFetchTechCompany = Boolean(mode && companyId)

  // This will only work if the mode is edit and id is present
  const { data: technologyCompanyData } = useQuery(
    ['technologyCompanyData', mode, companyId],
    async () => {
      const response = await TechnologyCompanyApi.getTechnologyCompanyById(
        faberToken,
        companyId
      )
      return response.data
    },
    {
      enabled: shouldFetchTechCompany,
      refetchOnMount: 'always',
      onError: () => {
        toast.error('Error searching technology companies')
      }
    }
  )

  // Update mutation
  const { mutate: updateTechnologyCompany ,isLoading: isUpdatingTechCompany } = useMutation(
    (data: TechCompanyBackendData) =>
      TechnologyCompanyApi.updateTechnologyCompany(data, faberToken),
    {
      onSuccess: () => {
        toast.success('Technology company updated successfully')
        // Invalidate the cache for search results
        queryClient.invalidateQueries({
          queryKey: ['searchTechnologyCompanies'],
          refetchType: 'none'
        })

        navigate('/hq/b2b/3ptp') // Navigate back to list
      },
      onError: () => {
        toast.error('Failed to update technology company')
      }
    }
  )

  const {
    control,
    watch,
    handleSubmit,
    reset,
    formState: { errors }
  } = useForm<FormValues>({
    defaultValues: {
      companyName: '',
      commissionType: commissionTypes[0],
      currency: CurrencySupported[0],
      value: ''
    }
  })

  const commissionType = watch('commissionType')

  const { mutate: createTechnologyCompany,isLoading: isCreatingTechCompany } = useMutation(
    (data: TechCompanyBackendData) =>
      TechnologyCompanyApi.saveTechnologyCompanyDetails(data, faberToken),
    {
      onSuccess: () => {
        toast.success('Catalog created successfully')
      },
      onError: () => {
        toast.error('Failed to create catalog')
      }
    }
  )
  //eslint-disable-next-line @typescript-eslint/no-explicit-any
  const onSubmit = (data: any) => {
    const payload: TechCompanyBackendData = {
      technology_company_name: data.companyName,
      commission: data.commissionType.value,
      currency: data?.currency?.value || '',
      value: data.value,
      commission_type: data.commissionType.value,
      is_active: true,
      created_by: userEmail
    }
    if (mode === 'edit' && companyId) {
      {
        payload.id = companyId
        payload.updated_by = userEmail
        updateTechnologyCompany(payload)
      }
    } else createTechnologyCompany(payload)
  }

  // Map the commission type for edit mode

  useEffect(() => {
    if (technologyCompanyData) {
      reset({
        companyName: technologyCompanyData.technology_company_name || '',
        commissionType: findByValue(
          commissionTypes,
          technologyCompanyData.commission_type
        ),
        currency: findByValue(
          CurrencySupported,
          technologyCompanyData.currency
        ),
        value: technologyCompanyData.value || ''
      })
    }
  }, [technologyCompanyData, reset])

  return (
    <Box>
      <form onSubmit={handleSubmit(onSubmit)}>
        <Box display='flex' alignItems='center'>
          <Typography sx={{ mr: '10px', fontSize: '14px' }}>
            Technology Company*
          </Typography>
          <Controller
            name='companyName'
            control={control}
            rules={{ required: { value: true, message: 'Required Name ' } }}
            render={({ field }) => (
              <TextField
                id='companyName'
                variant='outlined'
                size='small'
                error={!!errors.companyName}
                helperText={errors.companyName?.message}
                {...field}
              />
            )}
          />
        </Box>

        <Box display='flex' alignItems='center'>
          <Typography sx={{ mr: '10px', fontSize: '14px' }}>
            Commission
          </Typography>

          <Controller
            name='commissionType'
            control={control}
            render={({ field, fieldState: { error } }) => (
              <Autocomplete<SelectOption, false, true, false>
                options={commissionTypes}
                disableClearable
                disablePortal
                value={field.value ?? commissionTypes[0]}
                onChange={(_, opt) => field.onChange(opt)}
                getOptionLabel={(o) => o?.label ?? ''}
                size='small'
                sx={{ width: '30%' }}
                isOptionEqualToValue={(a, b) => a?.value === b?.value}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    // label='Commission Type'
                    error={!!error}
                    helperText={error?.message}
                  />
                )}
              />
            )}
          />
        </Box>

        {commissionType?.value == 'perTxFee' && (
          <Box display='flex' alignItems='center'>
            <Typography sx={{ mr: '10px', fontSize: '14px' }}>
              Currency*
            </Typography>
            <Controller
              name='currency'
              control={control}
              rules={{ required: { value: true, message: 'Required' } }}
              render={({ field, fieldState: { error } }) => (
                <Autocomplete<SelectOption, false, true, false> // third generic = disableClearable
                  disablePortal
                  disableClearable
                  options={CurrencySupported}
                  value={field.value ?? CurrencySupported[0]} // since disableClearable, avoid null
                  onChange={(_, option) => field.onChange(option)} // option is SelectOption
                  getOptionLabel={(o) => o?.label ?? ''}
                  isOptionEqualToValue={(a, b) => a?.value === b?.value}
                  size='small'
                  sx={{ width: '30%' }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      error={!!error}
                      helperText={error?.message}
                    />
                  )}
                />
              )}
            />
          </Box>
        )}

        {commissionType?.value !== 'select' && (
          <Box display='flex' alignItems='center'>
            <Typography sx={{ mr: '10px', fontSize: '14px' }}>
              Value*
            </Typography>
            <Controller
              name='value'
              control={control}
              rules={{ required: { value: true, message: 'Required value' } }}
              render={({ field }) => (
                <TextField
                  id='value'
                  variant='outlined'
                  size='small'
                  error={!!errors.value}
                  helperText={errors.value?.message}
                  {...field}
                />
              )}
            />
          </Box>
        )}

        <Box sx={{ mt: '20px' }}>
          <Button type='submit' variant='contained' disableElevation disabled={isCreatingTechCompany || isUpdatingTechCompany}>
            {isCreatingTechCompany || isUpdatingTechCompany ? 'Saving...' : 'Save'}
          </Button>
        </Box>
      </form>
    </Box>
  )
}

export default AddTechnologyCompany
